import React from 'react';
import { UseFormMethods } from 'react-hook-form';
import { SelectOptionElement } from '@odin-labs/components';
import { primaryLanguageOptions, toFancySelectOptions } from 'utils/constants';
import { RequiredField } from 'utils/validation';
import {
  FormInputTypes,
  GridColSpan,
  RadioGroupFormOption,
  TypedFormInputs,
} from 'components/form';
import {
  CreateAnnouncementData,
  FilterRecipientsFormData,
  JobsiteAccessStatusType,
  Jobsite,
} from 'containers/announcements/types';
import { defaultFromExisting } from 'containers/announcements/utils';
import { RecipientCounts } from 'containers/announcement/components';

const jobsiteAccessOptions: RadioGroupFormOption<
  Pick<FilterRecipientsFormData, 'filterAccessWithinDayRange'>,
  JobsiteAccessStatusType
>[] = [
  {
    label: 'Workers currently on site',
    value: JobsiteAccessStatusType.CurrentlyOnsite,
  },
  {
    label: 'Workers on site in the last X days',
    value: JobsiteAccessStatusType.RecentlyOnsite,
    inputs: [
      {
        name: 'filterAccessWithinDayRange',
        element: FormInputTypes.Field,
        elementProps: {
          fieldType: 'number',
        },
        validation: {
          required: {
            value: true,
            message: RequiredField`Date range`,
          },
        },
        layout: GridColSpan.SmSpan3,
      },
    ],
  },
  {
    label: 'All workers allowed on site',
    value: JobsiteAccessStatusType.AccessAllowed,
  },
];

type FilterRecipientsFormDataInput = {
  contractorOptions: SelectOptionElement[];
  announcement: CreateAnnouncementData;
  counts?: { totalReachableCount: number; totalCount: number };
  selectedJobsites?: Jobsite[];
  isWorkerAccessEnabled?: boolean;
  tradeOptions: SelectOptionElement[];
  titleOptions: SelectOptionElement[];
};

const languageOptions = toFancySelectOptions(primaryLanguageOptions);

export const getFormInputs = ({
  contractorOptions,
  counts,
  isWorkerAccessEnabled,
  tradeOptions,
  titleOptions,
}: FilterRecipientsFormDataInput): TypedFormInputs<FilterRecipientsFormData> => ({
  contractorIds: {
    element: FormInputTypes.Select,
    elementProps: {
      label: 'Contractor(s)',
      placeholder: 'All Contractors',
      options: contractorOptions,
      isMulti: true,
    },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
  },
  languages: {
    element: FormInputTypes.Select,
    elementProps: {
      label: 'Language',
      placeholder: 'Any Language',
      options: languageOptions,
      isMulti: true,
    },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
  },
  trades: {
    element: FormInputTypes.Select,
    elementProps: {
      label: 'Trade(s)',
      placeholder: 'All Trades',
      options: tradeOptions,
      isMulti: true,
    },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
  },
  titles: {
    element: FormInputTypes.Select,
    elementProps: {
      label: 'Title(s)',
      placeholder: 'All Titles',
      options: titleOptions,
      isMulti: true,
    },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
  },
  jobsiteAccessStatus: {
    element: FormInputTypes.RadioGroup,
    elementProps: {
      label: 'Jobsite Access Status',
      options: isWorkerAccessEnabled
        ? jobsiteAccessOptions
        : // `AccessAllowed` option which was filtered by index before,
          // changed its index from 0 to 2.
          // We directly filter by value here so that the correct option
          // is always selected.
          jobsiteAccessOptions.filter(
            (opt) => opt.value === JobsiteAccessStatusType.AccessAllowed,
          ),
    },
    validation: {
      required: true,
    },
    layout: [GridColSpan.SpanFull],
  },
  recipientCounts: {
    element: FormInputTypes.CustomContent,
    elementProps: {
      content: <RecipientCounts counts={counts} />,
    },
    layout: GridColSpan.SpanFull,
  },
});

type FilterRecipientsFormDataInputHook = FilterRecipientsFormDataInput & {
  updateAnnouncement: (data: Partial<CreateAnnouncementData>) => void;
  isWorkerAccessEnabled?: boolean;
};

export const getFormInputsHook =
  (args: FilterRecipientsFormDataInputHook) =>
  ({
    watch,
  }: UseFormMethods<FilterRecipientsFormData>): TypedFormInputs<FilterRecipientsFormData> => {
    const {
      contractorOptions,
      announcement,
      counts,
      updateAnnouncement,
      isWorkerAccessEnabled,
      tradeOptions,
      titleOptions,
    } = args;

    const {
      contractorIds,
      trades,
      titles,
      jobsiteAccessStatus,
      filterAccessWithinDayRange,
      languages,
    } = watch([
      'contractorIds',
      'trades',
      'titles',
      'languages',
      'jobsiteAccessStatus',
      'filterAccessWithinDayRange',
    ]);

    React.useEffect(() => {
      updateAnnouncement({
        ...(contractorIds
          ? { filterContractorIds: contractorIds.map(({ value }) => value) }
          : {}),
        ...(trades ? { filterTrades: trades.map(({ value }) => value) } : {}),
        ...(titles ? { filterTitles: titles.map(({ value }) => value) } : {}),
        ...(languages
          ? { filterLanguages: languages.map(({ value }) => value) }
          : {}),
        ...(jobsiteAccessStatus
          ? { filterJobsiteAccessStatus: jobsiteAccessStatus.value }
          : {}),
        filterAccessWithinDayRange: filterAccessWithinDayRange
          ? Number(filterAccessWithinDayRange)
          : undefined,
      });
    }, [
      contractorIds,
      trades,
      jobsiteAccessStatus,
      filterAccessWithinDayRange,
      languages,
      titles,
    ]);

    return React.useMemo(
      () =>
        getFormInputs({
          contractorOptions,
          announcement,
          counts,
          isWorkerAccessEnabled,
          tradeOptions,
          titleOptions,
        }),
      [
        contractorOptions,
        announcement,
        counts,
        isWorkerAccessEnabled,
        tradeOptions,
        titleOptions,
      ],
    );
  };

export const getDefaultValues = (
  announcement: CreateAnnouncementData,
  contractorOptions: SelectOptionElement[],
  tradeOptions: SelectOptionElement[],
  titleOptions: SelectOptionElement[],
): FilterRecipientsFormData => {
  return {
    contractorIds: defaultFromExisting(
      contractorOptions,
      announcement.filterContractorIds,
    ),
    languages: defaultFromExisting(
      languageOptions,
      announcement.filterLanguages,
    ),
    trades: defaultFromExisting(tradeOptions, announcement.filterTrades),
    titles: defaultFromExisting(titleOptions, announcement.filterTitles),
    jobsiteAccessStatus:
      jobsiteAccessOptions.find(
        (o) => o.value === announcement.filterJobsiteAccessStatus,
      ) ?? jobsiteAccessOptions[0],
    filterAccessWithinDayRange: announcement.filterAccessWithinDayRange,
    recipientCounts: undefined,
  };
};
